import { cnBase } from 'tailwind-variants';
import { useScreenSize } from '../../../hooks/useScreenSize';
import { useWindowBreakpoints } from '../../../hooks/useWindowBreakpoints';
import { Typography } from '../../molecules/Typography';

type TableHeader = {
  label: string;
  key: string;
  isPrimary?: boolean;
  style?: React.CSSProperties;
};

interface IResponsiveTableProps {
  headers: TableHeader[];
  data: any[];
  type?: 'desktop' | 'mobile' | 'auto';
  contentAlign?: 'center' | 'left' | 'right';
  className?: {
    table?: string;
    header?: string;
    row?: string;
    cell?: string;
    body?: string;
  };
}

function getResponsiveClasses(
  baseClasses: string,
  type: 'desktop' | 'mobile' | 'auto'
) {
  if (type === 'auto') return baseClasses;

  const classes = baseClasses.split(' ');

  if (type === 'desktop') {
    return classes
      .map((cls) => {
        if (cls === 'hidden') return '';
        if (cls.startsWith('md:')) {
          return cls.replace('md:', '');
        }
        return cls;
      })
      .join(' ');
  }

  if (type === 'mobile') {
    return classes.filter((cls) => !cls.startsWith('md:')).join(' ');
  }

  return baseClasses;
}

export function ResponsiveTable({
  data,
  headers,
  type = 'auto',
  contentAlign = 'center',
  className,
}: IResponsiveTableProps) {
  const screenSize = useScreenSize();
  const windowBreakpoint = useWindowBreakpoints();

  const isMobile = screenSize < windowBreakpoint.md;

  return (
    <table className={cnBase('w-full border-none', className?.table)}>
      <thead
        className={cnBase(
          getResponsiveClasses('hidden md:table-header-group', type),
          '!bg-[#434343]'
        )}
      >
        <tr
          className={cnBase('border-none bg-mono-color-light', className?.row)}
        >
          {headers.map((header, index) => (
            <th
              key={index}
              className={getResponsiveClasses(
                'border-none py-2 text-center first-of-type:text-left',
                type
              )}
              style={header?.style}
            >
              <Typography size='small' className='text-mono-color-darker'>
                {header.label}
              </Typography>
            </th>
          ))}
        </tr>
      </thead>

      <tbody className={cnBase('border-none', className?.body)}>
        {data.map((row, rowIndex) => {
          const primaryHeader = headers.find((header) => header.isPrimary);

          return (
            <tr
              key={rowIndex}
              className={getResponsiveClasses(
                'flex w-full flex-col gap-sm py-3 md:table-row md:border-b-hairline md:border-b-mono-color-lightest',
                type
              )}
            >
              {isMobile && primaryHeader && type !== 'desktop' && (
                <th className='flex w-full items-center justify-center border-none bg-mono-color-lightest p-0 '>
                  {row[primaryHeader.key]}
                </th>
              )}
              <div className='flex w-full gap-2 border-none md:contents'>
                {headers.map((header, colIndex) => {
                  if (header.isPrimary && isMobile && type !== 'desktop')
                    return null;

                  const isString =
                    typeof row[header.key] === 'string' ||
                    typeof row[header.key] === 'number';

                  return (
                    <td
                      key={colIndex}
                      className={getResponsiveClasses(
                        `flex flex-col items-center justify-center border-none p-0 px-1 text-${contentAlign} flex-1 md:table-cell`,
                        type
                      )}
                    >
                      {(isMobile && type !== 'desktop') || type === 'mobile' ? (
                        header.label ? (
                          <Typography
                            size='mini'
                            className={getResponsiveClasses(
                              `text-center md:text-base text-mono-color-medium mb-1`,
                              type
                            )}
                          >
                            {header.label}
                          </Typography>
                        ) : null
                      ) : null}
                      {isString ? (
                        <Typography
                          size='small'
                          className={getResponsiveClasses(
                            `py-4 md:text-base text-${contentAlign}`,
                            type
                          )}
                        >
                          {row[header.key]}
                        </Typography>
                      ) : (
                        row[header.key]
                      )}
                    </td>
                  );
                })}
              </div>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
